import axios from 'axios'
import { config } from '../../Constants'
import { parseJwt } from './Helpers'
import { PAYMENT_BLOCKED_CODE, savePaymentBlockedNotice } from '../payments/paymentBlockedNotice'

export const stockyApi = {
  authenticate,
  signup,
  numberOfUsers,
  getItems,
  getWarehouses,
  searchKitComponents,
  previewKitStock,
  createKit,
  getKit,
  undoKit,
  getItemsSummary,
  getUsers,
  updateAdminUser,
  updateUserSyncControls,
  updateUserBilling,
  updateUserProfile,
  blockUserForPayment,
  unblockUserForPayment,
  getFeatureFlags,
  updateFeatureFlag,
  getFeatureFlagUsers,
  setUserFeatureFlag,
  deleteUser,
  getUserMe,
  getNewAccountUrl,
  syncAccount,
  previewMercadoLibreSyncSelection,
  searchMarketplaceSyncSelection,
  searchMercadoLibreSyncSelection,
  importMarketplaceIds,
  importMercadoLibreMarketplaceIds,
  executeAccountSync,
  getAccountSyncStatus,
  getNotificationSummary,
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  getMarketplaceListingCandidates,
  dismissMarketplaceListingCandidate,
  importMarketplaceListingCandidates,
  updateItem,
  getCategories,
  publishMarketplaceItemRaw,
  retryPublication,
  previewListingCopy,
  copyListing,
  updateItems,
  getAttributes,
  getSellers,
  getItem,
  updateSelectedItems,
  bulkUpdateListingsInline,
  getOrders,
  exportOrdersCsv,
  retryOrderApplication,
  getOrder,
  getOrderSummary,
  markOrderRead,
  getStockMovements,
  exportStockMovementsCsv,
  getStockMovementMetadata,
  getStockMovementsSummary,
  getStockInconsistencySummary,
  getStockInconsistencies,
  getStockInconsistencyContext,
  detectStockInconsistencies,
  resolveStockInconsistency,
  updateListingInline,
  deleteListing,
  getSyncControlsMe,
  updateSyncControlsMe,
  getSyncControlsGlobal,
  updateSyncControlsGlobal,
  getPricingAccounts,
  downloadPricingImportTemplate,
  getPricingImportTemplateSummary,
  previewPricingImport,
  executePricingImport,
  updatePricingImportPrices,
  getPricingImportStatus,
  downloadStockImportTemplate,
  getStockImportTemplateSummary,
  previewStockImport,
  executeStockImport,
  updateStockImportStocks,
  getStockImportStatus,
  previewSkuMigrationImport,
  executeSkuMigrationImport,
  getSkuMigrationImportStatus,
  getListingFinancials,
  refreshListingFinancials,
  reconcileOrderFinancials,
}

function authenticate(username, password) {
  return instance.post('/auth/authenticate', { username, password }, {
    headers: { 'Content-type': 'application/json' }
  })
}

function signup(user, userAuthenticated) {
  return instance.post('/admin/signup', user, {
    headers: {
      'Content-type': 'application/json',
      'Authorization': bearerAuth(userAuthenticated)
    }
  })
}

function numberOfUsers() {
  return instance.get('/public/numberOfUsers')
}

function getItems(user, params = {}) {
  const allowedParams = new Set([
    'search',
    'sku',
    'kind',
    'warehouseId',
    'excludeItemId',
    'status',
    'marketplace',
    'sellerId',
    'listingId',
    'exclusiveToChannel',
    'page',
    'pageSize',
  ]);
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (!allowedParams.has(key)) {
      return;
    }
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value);
    }
  });
  const qs = query.toString();
  const url = qs ? `/api/items?${qs}` : '/api/items';

  return instance.get(url, {
    headers: { 'Authorization': bearerAuth(user) }
  })
}

function getWarehouses(user) {
  return instance.get('/api/warehouses', {
    headers: { Authorization: bearerAuth(user) },
  });
}

function searchKitComponents(user, params = {}) {
  return getItems(user, { ...params, kind: 'SIMPLE' });
}

function previewKitStock(user, payload) {
  return instance.post('/api/kits/preview-stock', payload, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: bearerAuth(user),
    },
  });
}

function createKit(user, payload) {
  return instance.post('/api/kits', payload, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: bearerAuth(user),
    },
  });
}

function getKit(user, kitId) {
  return instance.get(`/api/kits/${encodeURIComponent(kitId)}`, {
    headers: { Authorization: bearerAuth(user) },
  });
}

function undoKit(user, kitId) {
  return instance.post(`/api/kits/${encodeURIComponent(kitId)}/undo`, {}, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: bearerAuth(user),
    },
  });
}

function getUsers(user, username) {
  const url = username ? `/api/users/${username}` : '/api/users'
  return instance.get(url, {
    headers: { 'Authorization': bearerAuth(user) }
  })
}

function updateAdminUser(user, username, payload) {
  return instance.patch(`/api/users/${encodeURIComponent(username)}`, payload, {
    headers: {
      'Content-type': 'application/json',
      Authorization: bearerAuth(user),
    },
  });
}

function deleteUser(user, username) {
  return instance.delete(`/api/users/${username}`, {
    headers: { 'Authorization': bearerAuth(user) }
  })
}

function getUserMe(user) {
  return instance.get('/api/users/me', {
    headers: { 'Authorization': bearerAuth(user) }
  })
}

function getNewAccountUrl(accountType, user) {
  return instance.get(`/api/accounts?accountType=${accountType}`, {
    headers: { 'Authorization': bearerAuth(user) }
  })
}

function syncAccount(code, accountType, user, params = {}) {
  const query = new URLSearchParams({
    code,
    accountType,
  });
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value);
    }
  });

  return instance.get(`/api/accounts/sync?${query.toString()}`, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Authorization': bearerAuth(user)
    }
  })
}

function updateUserSyncControls(user, username, payload) {
  return instance.patch(`/api/users/${username}/sync-controls`, payload, {
    headers: {
      'Content-type': 'application/json',
      'Authorization': bearerAuth(user),
    },
  });
}

function updateUserProfile(user, username, payload) {
  return instance.patch(`/api/users/${username}/profile`, payload, {
    headers: {
      'Content-type': 'application/json',
      'Authorization': bearerAuth(user),
    },
  });
}

function blockUserForPayment(user, username) {
  return instance.post(`/api/users/${encodeURIComponent(username)}/payment-block`, null, {
    headers: { Authorization: bearerAuth(user) },
  });
}

function unblockUserForPayment(user, username) {
  return instance.delete(`/api/users/${encodeURIComponent(username)}/payment-block`, {
    headers: { Authorization: bearerAuth(user) },
  });
}

function updateUserBilling(user, username, payload) {
  return instance.patch(`/api/users/${username}/billing`, payload, {
    headers: {
      'Content-type': 'application/json',
      'Authorization': bearerAuth(user),
    },
  });
}

function getFeatureFlags(user) {
  return instance.get('/api/feature-flags', {
    headers: { 'Authorization': bearerAuth(user) },
  });
}

function updateFeatureFlag(user, featureKey, payload) {
  return instance.patch(`/api/feature-flags/${encodeURIComponent(featureKey)}`, payload, {
    headers: {
      'Content-type': 'application/json',
      'Authorization': bearerAuth(user),
    },
  });
}

function getFeatureFlagUsers(user, featureKey) {
  return instance.get(`/api/feature-flags/${encodeURIComponent(featureKey)}/users`, {
    headers: { 'Authorization': bearerAuth(user) },
  });
}

function setUserFeatureFlag(user, featureKey, username, enabled) {
  return instance.patch(
    `/api/feature-flags/${encodeURIComponent(featureKey)}/users/${encodeURIComponent(username)}`,
    { enabled },
    {
      headers: {
        'Content-type': 'application/json',
        'Authorization': bearerAuth(user),
      },
    },
  );
}

function previewMercadoLibreSyncSelection(code, user) {
  return instance.get(`/api/accounts/sync/preview/ml?code=${encodeURIComponent(code)}`, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user),
    },
  });
}

function searchMercadoLibreSyncSelection(user, params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value);
    }
  });
  return instance.get(`/api/accounts/sync/search/ml?${query.toString()}`, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user),
    },
  });
}

function searchMarketplaceSyncSelection(user, params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value);
    }
  });
  return instance.get(`/api/accounts/sync/search?${query.toString()}`, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user),
    },
  });
}

function importMercadoLibreMarketplaceIds(payload, user) {
  return instance.post('/api/accounts/sync/import/ml', payload, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user),
    },
  });
}

function importMarketplaceIds(payload, user) {
  return instance.post('/api/accounts/sync/import', payload, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user),
    },
  });
}

function executeAccountSync(payload, user) {
  return instance.post('/api/accounts/sync/execute', payload, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user),
    },
  });
}

function getAccountSyncStatus(syncId, user) {
  return instance.get(`/api/accounts/sync/status/${syncId}`, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Authorization': bearerAuth(user)
    }
  })
}

function getNotificationSummary(user, latest = 5) {
  return instance.get(`/api/notifications/summary?latest=${encodeURIComponent(latest)}`, {
    headers: {
      'Authorization': bearerAuth(user),
    },
  });
}

function getNotifications(user, params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value);
    }
  });
  const qs = query.toString();
  const url = qs ? `/api/notifications?${qs}` : '/api/notifications';
  return instance.get(url, {
    headers: {
      'Authorization': bearerAuth(user),
    },
  });
}

function markNotificationRead(notificationId, user) {
  return instance.patch(`/api/notifications/${notificationId}/read`, {}, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user),
    },
  });
}

function markAllNotificationsRead(user) {
  return instance.patch('/api/notifications/read-all', {}, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user),
    },
  });
}

function getMarketplaceListingCandidates(user, params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value);
    }
  });
  const qs = query.toString();
  const url = qs
    ? `/api/marketplace-listing-candidates?${qs}`
    : '/api/marketplace-listing-candidates';
  return instance.get(url, {
    headers: {
      'Authorization': bearerAuth(user),
    },
  });
}

function dismissMarketplaceListingCandidate(candidateId, user) {
  return instance.patch(`/api/marketplace-listing-candidates/${candidateId}/dismiss`, {}, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user),
    },
  });
}

function importMarketplaceListingCandidates(ids, user) {
  return instance.post('/api/marketplace-listing-candidates/import', { ids }, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user),
    },
  });
}

function updateItem(item, user) {

  const itemDto = mapToItemDto(item);

  return instance.put(`/api/items/${item.id}`, itemDto, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user)
    }
  });
}

function mapToItemDto(item) {
  return {
    id: item.id,
    variation_id: item.variationId,
    marketplace: item.marketplace,
    price: item.price,
    sku: item.sku,
    available_quantity: parseInt(item.availableQuantity, 10),
    seller_id: item.seller ? item.seller.id : 0,
    variations: (item.variations || []).map(variation => ({
      id: variation.id || '',
      price: variation.price || 0,
      quantity: variation.quantity || 0,
      //TODO: agregar en update request esta logica tambien
      default_picture_url: variation.default_picture_url || ''
    }))
  };
}

function getCategories(user, title) {
  const url = `/api/marketplace/categories?title=${title}`

  return instance.get(url , {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user)
    }
  })
}

function getAttributes(user, categoryId) {
  const url = `/api/marketplace/categories/${categoryId}/attributes`

  return instance.get(url , {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user)
    }
  })
}

function getSellers(user) {
  const url = `/api/sellers`;

  return instance.get(url , {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user)
    }
  })
}

function publishMarketplaceItemRaw(user, payload) {
  return instance.post('/api/marketplace/publish', payload, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user)
    }
  });
}

function retryPublication(user, jobId, payload) {
  return instance.post(`/api/publish-jobs/${encodeURIComponent(jobId)}/retry-publication`, payload, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user)
    }
  });
}

function previewListingCopy(user, payload) {
  return instance.post('/api/listings/copy/preview', payload, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user)
    }
  });
}

function copyListing(user, payload) {
  return instance.post('/api/listings/copy', payload, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user)
    }
  });
}

async function updateItems(items, user) {
    const url = '/api/items/bulk';

    const itemsDto = items.map(it => mapToItemDto(it))
    //console.log('itemsDto:', itemsDto)
    return instance.put(url, itemsDto, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': bearerAuth(user)
        }
    })
}

function getItem(marketplace, itemId, user, sellerId) {
  const url = `/api/marketplace/${marketplace}/items/${itemId}?sellerId=${sellerId}`;
  return instance.get(url, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user)
    }
  });
}

async function updateSelectedItems(item, selectedMarketplaceIds, user) {
  const payload = {
    marketplaceIds: (selectedMarketplaceIds || []).map((marketplaceId) => String(marketplaceId)),
    sku: item?.sku,
    price: item?.price !== undefined ? Number(item.price) : undefined,
    stock:
      item?.available_quantity !== undefined
        ? Math.max(0, Math.floor(Number(item.available_quantity)))
        : undefined,
  };
  return instance.patch('/api/listings/bulk-inline', payload, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user)
    }
  });
}

function bulkUpdateListingsInline(updates, user) {
  return instance.patch(
    '/api/listings/bulk-inline',
    {
      updates: (updates || []).map((update) => ({
        listingId: String(update?.listingId || ''),
        marketplaceId: update?.marketplaceId ? String(update.marketplaceId) : undefined,
        sku: update?.sku,
        price: update?.price,
        stock: update?.stock,
      })),
    },
    {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': bearerAuth(user)
      }
    },
  );
}

function getOrders(user, params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value);
    }
  });
  const qs = query.toString();
  const url = qs ? `/api/orders?${qs}` : '/api/orders';

  return instance.get(url, {
    headers: { 'Authorization': bearerAuth(user) }
  });
}

function exportReportCsv(path, user, params = {}, signal) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') query.append(key, value);
  });
  const qs = query.toString();
  return instance.get(qs ? `${path}?${qs}` : path, {
    headers: { 'Authorization': bearerAuth(user) },
    responseType: 'blob',
    signal,
  });
}

function exportOrdersCsv(user, params = {}, signal) {
  return exportReportCsv('/api/orders/export.csv', user, params, signal);
}

function retryOrderApplication(user, orderId) {
  return instance.post(
    `/api/orders/${encodeURIComponent(orderId)}/retry-application`,
    {},
    { headers: { 'Authorization': bearerAuth(user) } },
  );
}

function getOrder(user, orderId) {
  return instance.get(`/api/orders/${encodeURIComponent(String(orderId || '').trim())}`, {
    headers: { 'Authorization': bearerAuth(user) }
  });
}

function getOrderSummary(user) {
  return instance.get('/api/orders/summary', {
    headers: { 'Authorization': bearerAuth(user) }
  });
}

function markOrderRead(orderId, user) {
  return instance.patch(`/api/orders/${encodeURIComponent(String(orderId || '').trim())}/read`, {}, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user),
    },
  });
}

function getStockMovements(user, params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value);
    }
  });
  const qs = query.toString();
  const url = qs ? `/api/stock-movements?${qs}` : '/api/stock-movements';

  return instance.get(url, {
    headers: { 'Authorization': bearerAuth(user) }
  });
}

function exportStockMovementsCsv(user, params = {}, signal) {
  return exportReportCsv('/api/stock-movements/export.csv', user, params, signal);
}

function getStockInconsistencySummary(user, params = {}) {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value);
    }
  });
  const qs = query.toString();
  const url = qs ? `/api/stock-inconsistencies/summary?${qs}` : '/api/stock-inconsistencies/summary';
  return instance.get(url, {
    headers: { 'Authorization': bearerAuth(user) },
  });
}

function getStockInconsistencies(user, params = {}) {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    if (Array.isArray(value)) {
      value
        .map((entry) => String(entry || '').trim())
        .filter(Boolean)
        .forEach((entry) => query.append(key, entry));
      return;
    }
    query.append(key, value);
  });
  const qs = query.toString();
  const url = qs ? `/api/stock-inconsistencies?${qs}` : '/api/stock-inconsistencies';
  return instance.get(url, {
    headers: { 'Authorization': bearerAuth(user) },
  });
}

function getStockInconsistencyContext(user, inconsistencyId) {
  return instance.get(`/api/stock-inconsistencies/${encodeURIComponent(String(inconsistencyId || '').trim())}/context`, {
    headers: { 'Authorization': bearerAuth(user) },
  });
}

function detectStockInconsistencies(user, payload = {}) {
  return instance.post('/api/stock-inconsistencies/detect', payload, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user),
    },
  });
}

function resolveStockInconsistency(user, inconsistencyId, payload) {
  return instance.post(`/api/stock-inconsistencies/${inconsistencyId}/resolve`, payload, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user),
    },
  });
}

function getStockMovementsSummary(user, params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value);
    }
  });
  const qs = query.toString();
  const url = qs ? `/api/stock-movements/summary?${qs}` : '/api/stock-movements/summary';

  return instance.get(url, {
    headers: { 'Authorization': bearerAuth(user) }
  });
}

function getStockMovementMetadata(user, movementId) {
  return instance.get(`/api/stock-movements/${movementId}/metadata`, {
    headers: { 'Authorization': bearerAuth(user) }
  });
}

function updateListingInline(listingId, payload, user) {
  return instance.patch(`/api/listings/${listingId}/inline`, payload, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user),
    },
  });
}

function deleteListing(listingId, user) {
  return instance.delete(`/api/listings/${listingId}`, {
    headers: {
      'Authorization': bearerAuth(user),
    },
  });
}

function getSyncControlsMe(user) {
  return instance.get('/api/sync-controls/me', {
    headers: { 'Authorization': bearerAuth(user) },
  });
}

function updateSyncControlsMe(payload, user) {
  return instance.patch('/api/sync-controls/me', payload, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user),
    },
  });
}

function getSyncControlsGlobal(user) {
  return instance.get('/api/sync-controls/global', {
    headers: { 'Authorization': bearerAuth(user) },
  });
}

function updateSyncControlsGlobal(payload, user) {
  return instance.patch('/api/sync-controls/global', payload, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': bearerAuth(user),
    },
  });
}

function getPricingAccounts(user) {
  return instance.get('/api/pricing/accounts', {
    headers: { Authorization: bearerAuth(user) },
  });
}

function downloadPricingImportTemplate(sellerIds, importMode, filters, user) {
  return instance.post('/api/pricing/import-template', { sellerIds, importMode, ...filters }, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: bearerAuth(user),
    },
    responseType: 'arraybuffer',
  });
}

function getPricingImportTemplateSummary(sellerIds, importMode, filters, user) {
  return instance.post('/api/pricing/import-template/summary', { sellerIds, importMode, ...filters }, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: bearerAuth(user),
    },
  });
}

function previewPricingImport(payload, user) {
  return instance.post('/api/pricing/imports/preview', payload, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: bearerAuth(user),
    },
  });
}

function updatePricingImportPrices(draftId, edits, user) {
  return instance.patch(`/api/pricing/imports/${draftId}/prices`, { edits }, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: bearerAuth(user),
    },
  });
}

function executePricingImport(payload, user) {
  return instance.post('/api/pricing/imports/execute', payload, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: bearerAuth(user),
    },
  });
}

function getPricingImportStatus(importId, user) {
  return instance.get(`/api/pricing/imports/${importId}/status`, {
    headers: { Authorization: bearerAuth(user) },
  });
}

function downloadStockImportTemplate(filters, user) {
  return instance.post('/api/stock-import/import-template', filters, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: bearerAuth(user),
    },
    responseType: 'arraybuffer',
  });
}

function getStockImportTemplateSummary(filters, user) {
  return instance.post('/api/stock-import/import-template/summary', filters, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: bearerAuth(user),
    },
  });
}

function previewStockImport(payload, user) {
  return instance.post('/api/stock-import/imports/preview', payload, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: bearerAuth(user),
    },
  });
}

function updateStockImportStocks(draftId, edits, user) {
  return instance.patch(`/api/stock-import/imports/${draftId}/stocks`, { edits }, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: bearerAuth(user),
    },
  });
}

function executeStockImport(payload, user) {
  return instance.post('/api/stock-import/imports/execute', payload, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: bearerAuth(user),
    },
  });
}

function getStockImportStatus(importId, user) {
  return instance.get(`/api/stock-import/imports/${importId}/status`, {
    headers: { Authorization: bearerAuth(user) },
  });
}

function previewSkuMigrationImport(payload, user) {
  return instance.post('/api/stock-import/sku-migrations/preview', payload, {
    headers: { 'Content-type': 'application/json', 'Authorization': bearerAuth(user) },
  });
}

function executeSkuMigrationImport(payload, user) {
  return instance.post('/api/stock-import/sku-migrations/execute', payload, {
    headers: { 'Content-type': 'application/json', 'Authorization': bearerAuth(user) },
  });
}

function getSkuMigrationImportStatus(importId, user) {
  return instance.get(`/api/stock-import/sku-migrations/${importId}/status`, {
    headers: { 'Authorization': bearerAuth(user) },
  });
}

function getListingFinancials(listingIds, user, options = {}) {
  const query = new URLSearchParams();
  Array.from(new Set((listingIds || []).map((id) => String(id || '').trim()).filter(Boolean)))
    .forEach((listingId) => query.append('listingIds', listingId));
  return instance.get(`/api/pricing/listing-financials?${query.toString()}`, {
    headers: { Authorization: bearerAuth(user) },
    signal: options.signal,
  });
}

function getItemsSummary(user) {
  return instance.get('/api/items/summary', {
    headers: { 'Authorization': bearerAuth(user) },
  });
}

function refreshListingFinancials(payload, user, options = {}) {
  return instance.post('/api/pricing/listing-financials/refresh', payload, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: bearerAuth(user),
    },
    signal: options.signal,
  });
}

function reconcileOrderFinancials(payload, user) {
  return instance.post('/api/pricing/order-financials/reconcile', payload, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: bearerAuth(user),
    },
  });
}


const instance = axios.create({
  baseURL: config.url.API_BASE_URL
})

function clearStoredSession() {
  localStorage.removeItem('user')
  window.dispatchEvent(new Event('auth:session-changed'))
}

function createSessionExpiredError() {
  const error = new Error('Session expired')
  error.code = 'SESSION_EXPIRED'
  return error
}

instance.interceptors.request.use(function (config) {
  // If token is expired, remove it and let the app handle it (e.g. PrivateRoute will redirect)
  if (config.headers.Authorization) {
    try {
      const token = config.headers.Authorization.split(' ')[1]
      const data = parseJwt(token)
      if (!data?.exp || Date.now() > data.exp * 1000) {
        clearStoredSession()
        return Promise.reject(createSessionExpiredError())
      }
    } catch (error) {
      clearStoredSession()
      return Promise.reject(createSessionExpiredError())
    }
  }
  return config
}, function (error) {
  return Promise.reject(error)
})

instance.interceptors.response.use(function (response) {
  return response
}, function (error) {
  if (error.response && error.response.status === 401) {
    const data = error.response.data || {}
    if (data.code === PAYMENT_BLOCKED_CODE) {
      savePaymentBlockedNotice(data)
    }
    if (data.code === 'ACCOUNT_SUSPENDED' && data.message) {
      window.sessionStorage.setItem('auth:notice', data.message)
    }
    clearStoredSession()
  }
  return Promise.reject(error)
})

// -- Helper functions

function bearerAuth(user) {
  if (!user || !user.accessToken) {
    throw new Error('Not authenticated: missing access token');
  }
  return `Bearer ${user.accessToken}`
}


